import { useState, useEffect, useCallback } from 'react';
import { getMenuItems, createMenuItem, updateMenuItem, deleteMenuItem } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { MenuFormDialog } from '../components/menu/MenuFormDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, UtensilsCrossed, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const categories = ['breakfast', 'lunch', 'dinner', 'snacks', 'beverages'];

export default function Menu() {
  const { isAdmin } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'breakfast',
    price: '',
    available: true
  });

  const loadMenuItems = useCallback(async () => {
    try {
      const response = await getMenuItems(filterCategory || undefined);
      setMenuItems(response.data);
    } catch (error) {
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingItem) {
        await updateMenuItem(editingItem.id, data);
        toast.success('Menu item updated successfully');
      } else {
        await createMenuItem(data);
        toast.success('Menu item created successfully');
      }
      
      setModalOpen(false);
      setEditingItem(null);
      resetForm();
      loadMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category,
      price: item.price.toString(),
      available: item.available
    });
    setModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
    
    try {
      await deleteMenuItem(item.id);
      toast.success('Menu item deleted successfully');
      loadMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete menu item');
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await updateMenuItem(item.id, { available: !item.available });
      toast.success(`${item.name} is now ${!item.available ? 'available' : 'unavailable'}`);
      loadMenuItems();
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'breakfast',
      price: '',
      available: true
    });
  };

  const openAddModal = () => {
    setEditingItem(null);
    resetForm();
    setModalOpen(true);
  };

  const filteredItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedItems = categories.reduce((acc, category) => {
    acc[category] = filteredItems.filter(item => item.category === category);
    return acc;
  }, {});

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div data-testid="menu-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">F&B Menu</h1>
          <p className="text-stone-500 mt-1">Manage food and beverage items</p>
        </div>
        <Button 
          onClick={openAddModal}
          className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm"
          data-testid="add-menu-item-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search menu items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-menu-input"
          />
        </div>
        <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 border-stone-300" data-testid="filter-category-select">
            <Filter className="w-4 h-4 mr-2 text-stone-400" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Menu Items Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
          <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500">No menu items found</p>
          <Button 
            onClick={openAddModal}
            variant="outline"
            className="mt-4"
          >
            Add your first menu item
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {categories.map(category => {
            const items = groupedItems[category];
            if (items.length === 0 && filterCategory) return null;
            if (items.length === 0) return null;
            
            return (
              <div key={category}>
                <h2 className="text-lg font-semibold text-stone-900 font-['Manrope'] mb-4 capitalize">
                  {category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item, index) => (
                    <div 
                      key={item.id}
                      className={`bg-white border border-stone-200 rounded-md p-4 animate-fade-in ${!item.available ? 'opacity-60' : ''}`}
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`menu-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-stone-900 truncate">{item.name}</h3>
                          <p className="text-lg font-bold text-sage-700 font-['Manrope']">
                            {formatCurrency(item.price)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(item)}
                            className="h-8 w-8 p-0 text-stone-500 hover:text-sage-700 hover:bg-sage-50"
                            data-testid={`edit-menu-${item.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete(item)}
                              className="h-8 w-8 p-0 text-stone-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-menu-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {item.description && (
                        <p className="text-sm text-stone-500 mb-3 line-clamp-2">{item.description}</p>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                        <span className={`text-xs font-medium ${item.available ? 'text-emerald-700' : 'text-stone-400'}`}>
                          {item.available ? 'Available' : 'Unavailable'}
                        </span>
                        <Switch
                          checked={item.available}
                          onCheckedChange={() => toggleAvailability(item)}
                          className="data-[state=checked]:bg-emerald-700"
                          data-testid={`toggle-availability-${item.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <MenuFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editingItem={editingItem}
      />
    </div>
  );
}
